import { InteractionType } from '@azure/msal-browser';
import {
  MsalProvider,
  useMsal,
  useMsalAuthentication,
} from '@azure/msal-react';
import type { ReactNode } from 'react';
import { createContext, useState, useEffect } from 'react';
import OtpVerification from '../../components/otpValidation/OtpVerification';

import { msalInstance } from './config';
import { initializeTab, cleanupTab, isLastTab } from './tabManager';
import posthog from 'posthog-js';

type AuthContextType = object;
export const AuthContext = createContext<AuthContextType>(
  {} as AuthContextType,
);

type Props = { children: ReactNode };

const AuthenticatedRoute = ({ children }: Props) => {
  const { instance } = useMsal();
  useMsalAuthentication(InteractionType.Redirect);
  return <MsalProvider instance={instance}>{children}</MsalProvider>;
};

const AuthProvider = ({ children }: Props) => {
  const [i_ov, setIsOtpVerified] = useState(
    localStorage.getItem('i_ov') === 'true',
  );

  initializeTab();

  useEffect(() => {
    const handleBeforeUnload = () => {
      cleanupTab();
      if (isLastTab()) {
        localStorage.clear();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        setTimeout(() => {
          if (
            !Object.keys(sessionStorage).some(key => key.startsWith('tab_'))
          ) {
            localStorage.clear();
            setIsOtpVerified(false);
          }
        });
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () =>
      document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === 'i_ov') {
        setIsOtpVerified(event.newValue === 'true');
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  return (
    <MsalProvider instance={msalInstance}>
      <AuthContext.Provider value={{}}>
        <AuthenticatedRoute>
          {!i_ov && posthog.isFeatureEnabled('security-otp') ? (
            <OtpVerification
              onVerificationSuccess={() => {
                setIsOtpVerified(true);
                localStorage.setItem('i_ov', 'true');
              }}
            />
          ) : (
            children
          )}
        </AuthenticatedRoute>
      </AuthContext.Provider>
    </MsalProvider>
  );
};

export default AuthProvider;
